import produce from 'immer'
import * as t from './actionTypes'
import { actionTypes as nt } from 'features/navigation'
import { actionTypes as wt } from 'features/workspaces'
import { isSuccessAction, isErrorAction } from 'types'
import { ProductColorsState } from './types'
import createReducer from 'store/createReducer'
import { addItemToPagination } from 'utils/addItemToPagination'
import { removeItemFromPagination } from 'utils/removeItemFromPagination'
import {
  ChangeCurrentPageAction,
  ChangePageSizeAction,
  CreateProductColorAction,
  FetchProductColorAction,
  FetchProductColorsListAction,
  RemoveProductColorAction,
  UpdateProductColorAction,
  FetchProductColorsForOptionsAction
} from './actions'

const initState: ProductColorsState = {
  productColors: {},
  productColorsForOptions: [],
  pagination: {
    currentPage: 1,
    total: 0,
    pages: {},
    size: 10
  },
  UIState: {
    isCreateProductColor: false,
    isFetchProductColor: false,
    isListLoading: false,
    isRemoveProductColor: false,
    isUpdateProductColor: false,
    isProductColorsForOptionsLoaded: false
  }
}

const reducer = createReducer(initState, {
  [t.CREATE_PRODUCT_COLOR]: (state, action: CreateProductColorAction) =>
    produce(state, draft => {
      draft.UIState.isCreateProductColor = !(action.meta && action.meta.done)
      delete draft.UIState.error

      if (isErrorAction(action)) {
        draft.UIState.isCreateProductColor = false
        draft.UIState.error = action.payload
      }

      if (isSuccessAction(action)) {
        const { id } = action.payload
        draft.productColors[id] = action.payload
        addItemToPagination(draft.pagination, id)
      }
    }),

  [t.FETCH_PRODUCT_COLORS_LIST]: (state, action: FetchProductColorsListAction) =>
    produce(state, draft => {
      draft.UIState.isListLoading = !(action.meta && action.meta.done)
      delete draft.UIState.error

      if (isErrorAction(action)) {
        draft.UIState.isListLoading = false
        draft.UIState.error = action.payload
      }

      if (isSuccessAction(action)) {
        action.payload.items.forEach(item => {
          draft.productColors[item.id] = item
        })
        const { page, total, items } = action.payload
        draft.pagination.total = total
        draft.pagination.pages[page] = items.map(item => item.id)
      }
    }),

  [t.FETCH_PRODUCT_COLOR]: (state, action: FetchProductColorAction) =>
    produce(state, draft => {
      draft.UIState.isFetchProductColor = !(action.meta && action.meta.done)
      delete draft.UIState.error

      if (isErrorAction(action)) {
        draft.UIState.isFetchProductColor = false
        draft.UIState.error = action.payload
      }

      if (isSuccessAction(action)) {
        draft.productColors[action.payload.id] = action.payload
      }
    }),

  [t.REMOVE_PRODUCT_COLOR]: (state, action: RemoveProductColorAction) =>
    produce(state, draft => {
      draft.UIState.isRemoveProductColor = !(action.meta && action.meta.done)
      delete draft.UIState.error

      if (isErrorAction(action)) {
        draft.UIState.isRemoveProductColor = false
        draft.UIState.error = action.payload
      }

      if (isSuccessAction(action)) {
        if (draft.productColors[action.payload]) {
          delete draft.productColors[action.payload]
          removeItemFromPagination(draft.pagination, action.payload)
        }
      }
    }),

  [t.UPDATE_PRODUCT_COLOR]: (state, action: UpdateProductColorAction) =>
    produce(state, draft => {
      draft.UIState.isUpdateProductColor = !(action.meta && action.meta.done)
      delete draft.UIState.error

      if (isErrorAction(action)) {
        draft.UIState.isUpdateProductColor = false
        draft.UIState.error = action.payload
      }

      if (isSuccessAction(action)) {
        const { productColorId, productColorData } = action.payload
        if (draft.productColors[productColorId]) {
          draft.productColors[productColorId].name = productColorData.name
          draft.productColors[productColorId].displayName = productColorData.displayName
        }
      }
    }),

  [t.CHANGE_CURRENT_PAGE]: (state, action: ChangeCurrentPageAction) =>
    produce(state, draft => {
      if (action.payload) {
        draft.pagination.currentPage = action.payload
      }
    }),

  [t.CHANGE_PAGE_SIZE]: (state, action: ChangePageSizeAction) =>
    produce(state, draft => {
      if (action.payload) {
        draft.pagination.size = action.payload
      }
    }),

  [t.FETCH_PRODUCT_COLORS_FOR_OPTIONS]: (state, action: FetchProductColorsForOptionsAction) =>
    produce(state, draft => {
      draft.UIState.isProductColorsForOptionsLoaded = !(action.meta && action.meta.done)
      delete draft.UIState.error

      if (isErrorAction(action)) {
        draft.UIState.isProductColorsForOptionsLoaded = false
        draft.UIState.error = action.payload
      }

      if (isSuccessAction(action)) {
        draft.productColorsForOptions = action.payload
      }
    }),

  [wt.SWITCH_WORKSPACE]: state =>
    produce(state, draft => {
      draft.pagination = initState.pagination
    }),

  [nt.LOCATION_CHANGE]: state =>
    produce(state, draft => {
      if (draft.UIState.error) {
        delete draft.UIState.error
      }
    })
})

export default reducer
